import React from "react";
import PropTypes from "prop-types";

/**
 * 通用纵向 labeled-input 字段集渲染器。
 *
 * 与 KVTable (渲染 kv/kv_assert) / KvSimpleForm (渲染 kv_simple) 的关系：
 *   - KVTable 负责"可增删行的键值表"
 *   - KVInput 负责"静态的、有标签的 input/textarea 集合"
 *
 * 复用场景：
 *   1. PropertyPanel 里 section.type === "kv_input" 的通用段
 *      （典型用途：info 节点的"基本信息"段，含用例名称 / 描述）
 *
 * 字段定义（fields[*]）：
 *   - key: 稳定取值键
 *   - name: UI 展示名
 *   - type: "input" | "textarea"
 *   - placeholder: 占位符
 *   - default: 初始值
 *   - required: bool, UI 显示红色 *，空值时红框提示
 *
 * 受控值通过 values prop 传入，onChange(key, value) 回填。
 */
function KVInput({ fields, values, onChange, onValidate, isEdit }) {
  if (!fields || fields.length === 0) return null;

  const handleChange = (key, value) => {
    if (onChange) onChange(key, value);
  };

  return (
    <div className="kv-input">
      {fields.map((f) => {
        const isRequired = Boolean(f.required);
        const current = values?.[f.key] ?? f.default ?? "";
        const isEmpty = !current || (typeof current === "string" && current.trim() === "");
        const showError = isRequired && isEmpty;
        const commonProps = {
          className: "tfe-form-control kv-input-control",
          placeholder: f.placeholder ?? "",
          value: current,
          readOnly: !isEdit,
          "data-required": isRequired ? "true" : "false",
          "data-empty": showError ? "true" : "false",
          ref: (el) => {
            if (onValidate) onValidate(f.key, el);
          },
        };
        return (
          <div className="kv-input-field" key={f.key}>
            <label className="kv-input-label">
              {f.name}
              {isRequired && (
                <span
                  className="kv-input-required"
                  title="必填"
                  aria-label="必填"
                >
                  *
                </span>
              )}
            </label>
            {f.type === "textarea" ? (
              <textarea {...commonProps} rows={3} onChange={(e) => handleChange(f.key, e.target.value)} />
            ) : (
              <input {...commonProps} type="text" onChange={(e) => handleChange(f.key, e.target.value)} />
            )}
          </div>
        );
      })}
    </div>
  );
}

KVInput.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.oneOf(["input", "textarea"]),
      placeholder: PropTypes.string,
      default: PropTypes.string,
      required: PropTypes.bool,
    })
  ),
  /** 受控值对象：{ [key]: value } */
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  /** 收集每个字段的 DOM ref，供上层校验必填 */
  onValidate: PropTypes.func,
  isEdit: PropTypes.bool,
};

export default KVInput;